<template>
  <div class="grid grid-cols-1 gap-1 w-11/12 max-w-3xl mx-auto mt-4">
    <div v-for="checklist in checklists" class="border border-gray-200 rounded p-6 flex items-center justify-between">
      <div class="flex-grow">
        <header class="font-bold text-gray-900">{{ checklist.name }}</header>
      </div>
      <div class="flex gap-2 items-center">
        <router-link :to="setupTaskLink(checklist.id)">
          <TButton color="white" label="Setup Tasks" />
        </router-link>

        <ButtonMenu :iconOnly="true" :sections="2">
          <template v-slot:topmenu>
            <a @click="onEditClick(checklist)" :class="iconLinkClasses" role="menuitem">
              <vue-feather type="edit" :class="iconClasses" />
              Edit Checklist
            </a>
            <a @click="onCloneClick(checklist)" :class="iconLinkClasses" role="menuitem">
              <vue-feather type="copy" :class="iconClasses" />
              Clone Checklist
            </a>
          </template>
          <template v-slot:middlemenu>
            <a @click="onDeleteClick(checklist)" :class="iconLinkClasses" role="menuitem">
              <vue-feather type="trash" :class="iconClasses" />
              Delete Checklist
            </a>
          </template>
        </ButtonMenu>
      </div>
    </div>

    <UpsertModal
      formKind="edit"
      :taskName="taskName"
      :showModal="showEditModal"
      :initialName="selectedChecklist?.name"
      @nameInput="newChecklistName = $event"
      @action="updateChecklist"
      @closeModal="showEditModal = false"
    />

    <UpsertModal
      formKind="clone"
      :taskName="taskName"
      :showModal="showCloneModal"
      @nameInput="newChecklistName = $event"
      @action="cloneChecklist"
      @closeModal="showCloneModal = false"
    />

    <DeleteModal :showModal="showDeleteModal" @closeModal="showDeleteModal = false" @action="deleteChecklist" />

    <ZeroState v-if="!checklists || checklists.length === 0">
      <p>You do not have any {{ taskName.toLowerCase() }} checklists setup yet.</p>
    </ZeroState>
  </div>
</template>

<script>
import { Api } from '../../../../api';
import ButtonMenu from '../../../shared/ButtonMenu.vue';
import ZeroState from '../../../ZeroState.vue';
import UpsertModal from './UpsertModal.vue';
import DeleteModal from './DeleteModal.vue';

export default {
  components: { ZeroState, UpsertModal, DeleteModal, ButtonMenu },
  props: {
    taskName: { type: String, required: true },
    checklists: { type: Array },
    setupTaskLink: { type: Function, required: true },
  },
  data() {
    return {
      showEditModal: false,
      showCloneModal: false,
      showDeleteModal: false,
      selectedChecklist: null,
      newChecklistName: '',
      iconClasses: 'mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500',
      iconLinkClasses:
        'cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900',
    };
  },
  methods: {
    onEditClick(checklist) {
      this.selectedChecklist = checklist;
      this.newChecklistName = checklist.name;
      this.showEditModal = true;
    },
    onCloneClick(checklist) {
      this.selectedChecklist = checklist;
      this.newChecklistName = '';
      this.showCloneModal = true;
    },
    onDeleteClick(checklist) {
      this.selectedChecklist = checklist;
      this.showDeleteModal = true;
    },
    async updateChecklist() {
      const id = this.selectedChecklist.id;
      const name = this.newChecklistName;
      const result = await Api.TaskChecklists.updateTaskChecklist(id, name);

      this.$store.commit('general/hideLocalLoadingSpinner');

      if (result.errors && result.errors.length > 0) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Checklist save error',
          alertDescription: result.errors[0],
        });
      } else {
        this.selectedChecklist.name = this.newChecklistName;
        this.showEditModal = false;
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Checklist saved',
          alertDescription: 'Your checklist was successfully saved.  Use the button below to dismiss this alert.',
        });
      }
    },
    async cloneChecklist() {
      const id = this.selectedChecklist.id;
      const name = this.newChecklistName;
      const result = await Api.TaskChecklistClones.create(id, name);

      this.$store.commit('general/hideLocalLoadingSpinner');

      if (result.errors && result.errors.length > 0) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Checklist clone error',
          alertDescription: result.errors,
        });
      } else {
        this.$emit('refresh');
        this.showCloneModal = false;
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Checklist cloned',
          alertDescription: 'Your checklist was successfully cloned.  Use the button below to dismiss this alert.',
        });
      }
    },
    async deleteChecklist() {
      try {
        const id = this.selectedChecklist.id;
        const { status } = await Api.TaskChecklists.deleteTaskChecklist(id);

        this.$store.commit('general/hideLocalLoadingSpinner');

        if (status === 'success') {
          this.$emit('refresh');
          this.showDeleteModal = false;
          this.$store.commit('general/showAlert', {
            alertType: 'success',
            alertTitle: 'Checklist Deleted',
            alertDescription: 'Your checklist was successfully deleted.',
          });
        }
      } catch (error) {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Error',
          alertDescription: 'Failed to delete checklist. Please try again.',
        });
      }
    },
  },
};
</script>
